// context/VehicleContext.js
// Loads client vehicles once at login, shares across all components.
// Mutations (add/edit/delete/archive) update both context and Supabase immediately.

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";
import { clientVinService } from "../services/clientVinService";
import { useAuth } from "./AuthContext";

const VehicleContext = createContext();

export function VehicleProvider({ children }) {
  const { user } = useAuth();

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const lastClientIdRef = useRef(null);

  // Load vehicles once when clientId becomes available
  useEffect(() => {
    if (!user?.clientId) {
      // User logged out or not a client — reset
      if (lastClientIdRef.current) {
        setVehicles([]);
        setLoaded(false);
        setLoading(true);
        lastClientIdRef.current = null;
      }
      return;
    }

    // Already loaded for this client
    if (user.clientId === lastClientIdRef.current) return;

    let cancelled = false;
    lastClientIdRef.current = user.clientId;

    const load = async () => {
      setLoading(true);
      try {
        const result = await clientVinService.loadClientVehicles();
        if (cancelled) return;
        if (result.success) {
          setVehicles(result.data || []);
        } else {
          // Fallback to sessionStorage
          const local = clientVinService.getSavedVehicles() || [];
          setVehicles(local);
        }
      } catch {
        if (cancelled) return;
        const local = clientVinService.getSavedVehicles() || [];
        setVehicles(local);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoaded(true);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.clientId]);

  // Force refresh from Supabase (e.g. after add/edit/delete)
  const refreshVehicles = useCallback(async () => {
    try {
      const result = await clientVinService.loadClientVehicles();
      if (result.success) {
        setVehicles(result.data || []);
      }
    } catch {
      // Keep current state on error
    }
  }, []);

  // Optimistic update: add a vehicle to context immediately
  const addVehicle = useCallback((vehicle) => {
    setVehicles((prev) => [vehicle, ...prev]);
  }, []);

  // Optimistic update: update a vehicle in context immediately
  const updateVehicle = useCallback((vehicleId, updatedData) => {
    setVehicles((prev) =>
      prev.map((v) => (v.id === vehicleId ? { ...v, ...updatedData } : v))
    );
  }, []);

  // Optimistic update: remove a vehicle from context (archived/deleted)
  const removeVehicle = useCallback((vehicleId) => {
    setVehicles((prev) => prev.filter((v) => v.id !== vehicleId));
  }, []);

  const value = {
    vehicles,
    vehicleCount: vehicles.length,
    loading,
    loaded,
    refreshVehicles,
    addVehicle,
    updateVehicle,
    removeVehicle,
  };

  return (
    <VehicleContext.Provider value={value}>
      {children}
    </VehicleContext.Provider>
  );
}

export function useVehicles() {
  const context = useContext(VehicleContext);
  if (!context) {
    throw new Error("useVehicles must be used within a VehicleProvider");
  }
  return context;
}

export default VehicleContext;
